import {Component, inject, Inject, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {AppointmentType, Appointment, CreatedPatient, Patient, PracticeLocation, QueryPatient, AppointmentMetadata, SelectOption, instantToPracticeWallDate, pickerToWallClock, PracticeTimezoneService} from '@xmedical/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {DatePipe, NgTemplateOutlet} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {CustomValidators, XmedicalUiModule, ConfirmationDialogComponent} from '@xmedical/ui';
import {CALENDAR_DATA_PROVIDER} from '../../tokens/calendar.tokens';
import {AppointmentClashBannerComponent} from '../appointment-clash-banner/appointment-clash-banner.component';
import dayjs from 'dayjs/esm';

/** The patient-acquisition mode the dialog is in, derived entirely from the appointment type. */
type PatientMode = 'none' | 'existing' | 'new';

function endAfterStartValidator(control: AbstractControl): ValidationErrors | null {
  const start = control.get('date')?.value;
  const end = control.get('endDate')?.value;
  if (!start || !end) return null;

  const startDate = dayjs(start);
  const endDate = dayjs(end);
  if (!startDate.isValid() || !endDate.isValid()) return null;

  if (endDate.valueOf() < startDate.valueOf()) {
    return {endBeforeStart: true};
  }
  return null;
}

export interface NewAppointmentDialogData {
  appointmentTypes: AppointmentType[];
  selectedDate: Date;
  selectedEndDate?: Date;
  newAppointment: Appointment;
  intervals: Date[];
  practiceLocationId?: number;
  practicePractitionerId?: number;
}

@Component({
  selector: 'app-new-appointment',
  templateUrl: './new-appointment.component.html',
  styleUrls: ['./new-appointment.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatDividerModule,
    MatIconModule,
    NgTemplateOutlet,
    XmedicalUiModule,
    AppointmentClashBannerComponent,
  ],
  providers: [DatePipe]
})
export class NewAppointmentComponent implements OnInit, OnDestroy {

  validate = false;
  newPatientValidate = false;
  form: UntypedFormGroup;
  /** Inline "new patient" capture form, shown for new-patient appointment types. */
  newPatientForm: UntypedFormGroup;
  selectedDate: Date = new Date();
  appointmentTypes: AppointmentType[] = [];
  patient?: QueryPatient = null;
  selectedType: AppointmentType = null;
  saving = false;
  /** Which new-patient save path the toggle selects: create a record, or quick (meta-only) booking.
   *  Seeded in ngOnInit from the practice's defaultPatientAppointmentsWithoutPatient setting. */
  newPatientSaveMode: 'record' | 'quick' = 'record';
  /** Possible existing matches for the identity number typed into the new-patient form. */
  duplicates: QueryPatient[] = [];
  duplicatesOpen = false;
  /** Surfaced when the appointment failed to book (the patient may already have been created). */
  saveError: string | null = null;
  /** Set once createCalendarPatient succeeds; a retry after a booking failure re-books against
   *  this patient instead of creating a duplicate. */
  private createdPatientId: number | null = null;

  private dataProvider = inject(CALENDAR_DATA_PROVIDER);
  private practiceTz = inject(PracticeTimezoneService);

  private appointmentsRepositorySubscription: Subscription;
  private formSubscription: Subscription;
  private dateChangeSubscription: Subscription;
  private identitySubscription: Subscription;
  private dedupeWatchSubscription: Subscription;
  private dedupeSubscription: Subscription;
  private createPatientSubscription: Subscription;
  private typeChangeSubscription: Subscription;

  constructor(
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<NewAppointmentComponent>,
    @Inject(MAT_DIALOG_DATA) private data: NewAppointmentDialogData,
    public datePipe: DatePipe,
    private formBuilder: UntypedFormBuilder,
  ) {
  }

  get allowPatientAppointmentsWithoutPatient(): boolean {
    return this.dataProvider.practiceSettings()?.allowPatientAppointmentsWithoutPatient ?? false;
  }

  /** Title options for the inline new-patient form (empty if the app doesn't support inline create). */
  get patientTitleOptions(): SelectOption[] {
    return this.dataProvider.patientTitles?.() ?? [];
  }

  /** The patient-acquisition mode for a given type. Intent lives on the type, not in the user's head. */
  private modeOf(type: AppointmentType | null): PatientMode {
    if (!type?.isPatientAppointment) return 'none';
    return type.isNewPatientAppointment ? 'new' : 'existing';
  }

  /** True when the selected type is a "new patient" type — show the inline capture form, hide search. */
  get isNewPatientType(): boolean {
    return this.modeOf(this.selectedType) === 'new';
  }

  /** True when the selected type is a normal patient type — search and select an existing patient. */
  get isExistingPatientType(): boolean {
    return this.modeOf(this.selectedType) === 'existing';
  }

  /** True when the selected type is not a patient appointment — shows the free-text Label only. */
  get isNonPatientType(): boolean {
    return this.modeOf(this.selectedType) === 'none';
  }

  /** True once the patient was created but its booking failed: the inline form is locked to that
   *  patient (details can't be edited) and a retry just re-books against it. */
  get newPatientLocked(): boolean {
    return this.createdPatientId !== null;
  }

  get hasDuplicates(): boolean {
    return this.duplicates.length > 0;
  }

  get practiceSettings() {
    return this.dataProvider.practiceSettings();
  }

  /** Bookable locations for this practice (deleted ones hidden). */
  get activeLocations(): PracticeLocation[] {
    return (this.dataProvider.practiceLocations?.() ?? []).filter(location => !location.isDeleted);
  }

  /** Only offer a location choice when the practice actually has more than one. */
  get showLocationSelector(): boolean {
    return this.activeLocations.length > 1;
  }

  /** Location used for the clash check — the currently chosen one (or the seed). */
  get clashLocationId(): number | null {
    return this.form?.get('practiceLocationId')?.value ?? this.data.practiceLocationId ?? null;
  }

  /** Practitioner used for the clash check. Practitioner is never chosen in this dialog:
   *  use the calendar's selected practitioner, falling back to the practice's primary
   *  practitioner — the same one the booking resolves to when none is selected. */
  get clashPractitionerId(): number | null {
    return this.data.practicePractitionerId ?? this.primaryPractitionerId;
  }

  private get primaryPractitionerId(): number | null {
    const practitioners = (this.dataProvider.practicePractitioners?.() ?? []).filter(p => !p.isDeleted);
    const primary = practitioners.find(p => p.isPrimary) ?? practitioners[0];
    return primary?.id ?? null;
  }

  /** Seed the location: the calendar's active location filter when set, else the
   *  practice default, else the first bookable location. */
  private defaultLocationId(): number | null {
    const locations = this.activeLocations;
    if (this.data.practiceLocationId && locations.some(location => location.id === this.data.practiceLocationId)) {
      return this.data.practiceLocationId;
    }
    const fallback = locations.find(location => location.isDefault) ?? locations[0];
    return fallback?.id ?? null;
  }

  get formValid(): boolean {
    if (!this.form) {
      return false;
    }
    return this.form.valid || false;
  }

  get hasPatient(): boolean {
    return this.patient !== null;
  }

  get appointmentMetaData(): UntypedFormGroup {
    return this.form.get('appointmentMetaData') as UntypedFormGroup;
  }

  ngOnInit(): void {
    // selectedDate / selectedEndDate arrive as true instants (from the calendar
    // grid, day-view slot, or FAB). Convert to practice wall-clock so the
    // timezone-unaware datetime picker shows practice time; on save we send that
    // wall-clock verbatim (pickerToWallClock). For an in-zone viewer these are no-ops.
    const zone = this.practiceTz.practiceTimezone();
    this.selectedDate = instantToPracticeWallDate(this.data.selectedDate, zone);
    this.appointmentTypes = (this.data.appointmentTypes ?? []).filter(t => t.canCreate !== false);
    this.selectedType = this.appointmentTypes[0] ?? null;

    const defaultDuration = this.dataProvider.practiceSettings()?.defaultAppointmentDurationMinutes ?? 30;
    const endDate = this.data.selectedEndDate
      ? instantToPracticeWallDate(this.data.selectedEndDate, zone)
      : new Date(this.selectedDate.getTime() + defaultDuration * 60 * 1000);

    this.form = this.formBuilder.group({
      title: [this.selectedType?.title ?? '', Validators.maxLength(100)],
      patientId: [''],
      date: [this.selectedDate, Validators.required],
      endDate: [endDate, Validators.required],
      comment: ['', Validators.maxLength(250)],
      practiceLocationId: [this.defaultLocationId()],
      appointmentType: [this.selectedType?.id, Validators.required],
      appointmentMetaData: this.formBuilder.group({
        name: [''],
        cell: [''],
        email: [''],
      })
    }, {validators: endAfterStartValidator});
    this.buildNewPatientForm();
    // Seed the booking mode from the practice default, then sync which controls are
    // active so form validity reflects only the fields the chosen mode shows.
    this.applySaveMode(this.defaultSaveMode());

    this.formSubscription = this.form.get('appointmentType').valueChanges.subscribe(val => {
      const nextType = this.appointmentTypes.find(t => t.id === val) ?? null;
      // Warn before a type change discards patient details the user already entered.
      if (this.wouldLoseDataOnTypeChange(nextType)) {
        this.confirmTypeChange(nextType);
      } else {
        this.applyTypeChange(nextType);
      }
    });

    // When start changes, shift end to maintain duration
    this.dateChangeSubscription = this.form.get('date').valueChanges.subscribe(newStart => {
      if (!newStart) return;
      const currentStart = dayjs(this.selectedDate);
      const currentEnd = dayjs(this.form.get('endDate').value);
      if (currentStart.isValid() && currentEnd.isValid()) {
        const durationMs = currentEnd.valueOf() - currentStart.valueOf();
        this.form.get('endDate').setValue(dayjs(newStart).add(durationMs, 'millisecond').toDate(), {emitEvent: false});
      }
      this.selectedDate = dayjs(newStart).toDate();
    });
  }

  ngOnDestroy(): void {
    this.appointmentsRepositorySubscription?.unsubscribe();
    this.formSubscription?.unsubscribe();
    this.dateChangeSubscription?.unsubscribe();
    this.identitySubscription?.unsubscribe();
    this.dedupeWatchSubscription?.unsubscribe();
    this.dedupeSubscription?.unsubscribe();
    this.createPatientSubscription?.unsubscribe();
    this.typeChangeSubscription?.unsubscribe();
  }

  /**
   * Standard save: a non-patient appointment, a selected existing patient, or the
   * pre-existing meta-only ("Add Patient Details") path. Unchanged behaviour.
   */
  save() {
    this.validate = true;
    // An existing-patient appointment must have a selected patient — never save one with a null
    // patientId (the form has no patientId validator, so guard it here).
    if (this.isExistingPatientType && !this.hasPatient) {
      return;
    }
    if (!this.form.valid) {
      return;
    }
    const meta = this.form.value.appointmentMetaData;
    const appointment = this.buildAppointment(
      this.patient?.id ?? null,
      this.form.value.title,
      {name: meta.name, cell: meta.cell, email: meta.email},
    );
    this.saving = true;
    this.createAppointmentAndClose(appointment);
  }

  /**
   * Single new-patient booking action. The segmented toggle decides whether we create a
   * patient record or do a quick (meta-only) booking; when the practice doesn't allow
   * patient-less appointments there is no choice and we always create the record.
   */
  bookNewPatient() {
    if (this.allowPatientAppointmentsWithoutPatient && this.newPatientSaveMode === 'quick') {
      this.saveWithoutPatient();
    } else {
      this.saveWithNewPatient();
    }
  }

  /**
   * New-patient full create: commit the patient first, then book against it. If the
   * booking call fails after the patient is created we keep the patient and surface
   * the error so the user can retry (there is no atomic create-patient-and-book endpoint).
   */
  saveWithNewPatient() {
    this.validate = true;
    this.newPatientValidate = true;

    // A previous attempt already created the patient and only the booking failed: the form is
    // locked, so re-book against that patient — don't re-create it, and don't re-validate the
    // now-disabled fields.
    if (this.createdPatientId !== null) {
      this.saving = true;
      this.saveError = null;
      this.bookCreatedPatient(this.createdPatientId);
      return;
    }

    if (!this.form.valid || !this.newPatientForm.valid || !this.dataProvider.createCalendarPatient) {
      return;
    }
    this.saving = true;
    this.saveError = null;
    const patient = this.newPatientForm.value as Patient;
    this.createPatientSubscription = this.dataProvider.createCalendarPatient(patient).subscribe({
      next: (created: CreatedPatient) => {
        this.createdPatientId = created.id;
        this.bookCreatedPatient(created.id);
      },
      error: (error) => {
        console.error(error);
        this.saving = false;
        this.saveError = 'The patient could not be created. Please check the details and try again.';
      }
    });
  }

  /** Book the appointment against an already-created patient. On booking failure keep the patient
   *  (createdPatientId is retained) and lock the form so its details can't drift from the created
   *  record — a retry just re-books against it. */
  private bookCreatedPatient(patientId: number) {
    const appointment = this.buildAppointment(patientId, this.composeName(), this.metaFromNewPatientForm());
    this.createAppointmentAndClose(appointment, () => {
      this.saveError = 'The patient was created but the appointment could not be booked. Please try booking again.';
      this.newPatientForm.disable({emitEvent: false});
    });
  }

  /**
   * New-patient meta-only save: book the slot with just the captured contact details,
   * no patient record. Only available when the practice allows appointments without a patient.
   */
  saveWithoutPatient() {
    this.validate = true;
    this.newPatientValidate = true;
    if (!this.form.valid || !this.newPatientForm.valid) {
      return;
    }
    this.saving = true;
    this.saveError = null;
    const meta = this.metaFromNewPatientForm();
    const appointment = this.buildAppointment(null, meta.name, meta);
    this.createAppointmentAndClose(appointment);
  }

  /** Build the appointment payload from the shared form fields plus the supplied patient context. */
  private buildAppointment(patientId: number | null, title: string, metaData: AppointmentMetadata): Appointment {
    const details = this.form.value;
    // Appointment times are zone-less wall-clock; send the wall the user picked
    // verbatim (never a converted instant).
    return {
      title,
      start: pickerToWallClock(details.date),
      end: pickerToWallClock(details.endDate),
      comment: details.comment,
      patientId,
      practiceId: this.dataProvider.selectedPracticeId(),
      practiceLocationId: details.practiceLocationId ?? this.data.practiceLocationId,
      practicePractitionerId: this.data.practicePractitionerId,
      deleted: false,
      appointmentType: this.selectedType,
      appointmentMetaData: metaData,
    } as Appointment;
  }

  private createAppointmentAndClose(appointment: Appointment, onError?: () => void) {
    this.appointmentsRepositorySubscription = this.dataProvider.createAppointment(appointment).subscribe({
      next: () => {
        this.saving = false;
        this.dialogRef.close(appointment);
      },
      error: (error) => {
        console.error(error);
        this.saving = false;
        onError?.();
      }
    });
  }

  /** Patient display name composed from the inline form (used for the appointment title + metadata).
   *  getRawValue so it still reads after the form is locked on a partial-create retry. */
  private composeName(): string {
    const value = this.newPatientForm?.getRawValue() ?? {};
    return [value.firstName, value.surname].filter(part => !!part).join(' ').trim();
  }

  private metaFromNewPatientForm(): AppointmentMetadata {
    const value = this.newPatientForm?.getRawValue() ?? {};
    return {
      name: this.composeName(),
      cell: value.cell?.number ?? '',
      email: value.email?.emailAddress ?? '',
    } as AppointmentMetadata;
  }

  /** Adopt an existing patient surfaced by the duplicate check instead of creating a new one. */
  useExistingPatient(patient: QueryPatient) {
    this.duplicates = [];
    this.duplicatesOpen = false;
    this.patientSelected(patient);
  }

  toggleDuplicatesOpen() {
    this.duplicatesOpen = !this.duplicatesOpen;
  }

  close() {
    this.dialogRef.close();
  }

  searchPatients = (query: string): Observable<QueryPatient[]> => {
    return this.dataProvider.searchPatients(query.toLowerCase());
  }

  clearPatient() {
    this.patient = null;
    this.form.get('patientId').setValue(null);
    this.form.get('patientId').updateValueAndValidity();
    this.appointmentMetaData.reset();
  }

  patientSelected(patient: QueryPatient) {
    this.patient = patient;
    this.form.get('patientId').setValue(patient.id);
    this.form.get('patientId').updateValueAndValidity();
    this.form.get('title').setValue(patient.formalName ?? patient.name ?? this.form.get('title').value);
    this.form.get('title').updateValueAndValidity();
  }

  /** Build the inline new-patient capture form. Mirrors the standalone calendar new-patient form.
   *  Both modes capture title/firstName/surname; only the record mode adds the ID + confirm
   *  block (enabled/disabled by applySaveMode), which is the sole field difference between them. */
  private buildNewPatientForm() {
    this.newPatientForm = this.formBuilder.group({
      title: ['', Validators.required],
      firstName: ['', Validators.required],
      surname: ['', Validators.required],
      identityNumber: ['', [CustomValidators.identityNumberValidator(true)]],
      identityNumberConfirmation: [''],
      email: this.formBuilder.group({
        type: [0],
        label: [''],
        emailAddress: [''],
        emailAddressConfirm: [''],
      }),
      cell: this.formBuilder.group({
        type: [2, Validators.required],
        label: ['Cell', Validators.required],
        number: ['', Validators.required],
      }),
    }, {validators: [CustomValidators.duplicateFieldMatchRequired('identityNumber', 'identityNumberConfirmation')]});

    const identityChanges = this.newPatientForm.get('identityNumber').valueChanges;
    // Default the title from the ID's encoded gender as soon as it's complete (cheap, local).
    this.identitySubscription = identityChanges.subscribe(() => this.applyTitleFromIdentity());
    // Warn about possible duplicates as the identity number is typed (single-practice
    // analog of the org flow's cross-practice existence check). Debounced — it hits the API.
    this.dedupeWatchSubscription = identityChanges
      .pipe(debounceTime(1500), distinctUntilChanged())
      .subscribe(() => this.checkDuplicates());
  }

  /** The booking mode a fresh new-patient form opens in, from the practice default. Only the
   *  quick path exists when the practice allows patient-less appointments. */
  private defaultSaveMode(): 'record' | 'quick' {
    return this.allowPatientAppointmentsWithoutPatient && (this.practiceSettings?.defaultPatientAppointmentsWithoutPatient ?? false)
      ? 'quick'
      : 'record';
  }

  /** Switch the booking mode and enable only the ID fields the mode shows, so form validity
   *  reflects the visible fields. Both modes capture title/firstName/surname; only record mode
   *  adds the identity number + confirmation. */
  applySaveMode(mode: 'record' | 'quick') {
    this.newPatientSaveMode = mode;
    const recordOnly = ['identityNumber', 'identityNumberConfirmation'];
    if (mode === 'quick') {
      recordOnly.forEach(name => this.newPatientForm.get(name).disable({emitEvent: false}));
    } else {
      recordOnly.forEach(name => this.newPatientForm.get(name).enable({emitEvent: false}));
    }
  }

  /** Default the title select from the SA ID's encoded gender, unless the user has already
   *  chosen a title themselves (control still pristine). Editable either way. */
  private applyTitleFromIdentity() {
    const titleControl = this.newPatientForm.get('title');
    if (!titleControl.pristine) {
      return;
    }
    const gender = CustomValidators.genderFromSaId(this.newPatientForm.get('identityNumber').value);
    if (!gender) {
      return;
    }
    const preferences = gender === 'M' ? ['Mr'] : ['Ms', 'Mrs', 'Miss'];
    const option = preferences
      .map(text => this.patientTitleOptions.find(o => o.text === text))
      .find(Boolean);
    if (option) {
      titleControl.setValue(option.value, {emitEvent: false});
    }
  }

  private checkDuplicates() {
    const identityNumber = (this.newPatientForm.get('identityNumber').value || '').trim();
    if (!identityNumber) {
      this.duplicates = [];
      this.duplicatesOpen = false;
      return;
    }
    this.dedupeSubscription?.unsubscribe();
    this.dedupeSubscription = this.dataProvider.searchPatients(identityNumber).subscribe({
      next: (results) => {
        // A search in flight when the user keeps typing can resolve after the identity number
        // has changed; ignore its results so we never show (and let them "Use") a match for an
        // identity number that is no longer in the form.
        if ((this.newPatientForm.get('identityNumber').value || '').trim() !== identityNumber) {
          return;
        }
        this.duplicates = results ?? [];
        this.duplicatesOpen = this.duplicates.length > 0 && this.duplicates.length < 5;
      },
      error: (error) => console.error(error),
    });
  }

  /** True if entered patient details would be lost by moving to a different patient mode. */
  private wouldLoseDataOnTypeChange(nextType: AppointmentType | null): boolean {
    const hasData = this.hasPatient || (this.newPatientForm?.dirty ?? false);
    if (!hasData) {
      return false;
    }
    return this.modeOf(this.selectedType) !== this.modeOf(nextType);
  }

  private confirmTypeChange(nextType: AppointmentType | null) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Change appointment type?',
        message: 'Changing the appointment type will discard the patient details you have entered.',
      }
    });
    this.typeChangeSubscription = dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.resetPatientState();
        this.applyTypeChange(nextType);
      } else {
        // Revert the dropdown to the current type without re-triggering this guard.
        this.form.get('appointmentType').setValue(this.selectedType?.id, {emitEvent: false});
      }
    });
  }

  private applyTypeChange(nextType: AppointmentType | null) {
    this.selectedType = nextType;
    // Only a non-patient Label adopts the appointment-type title. For a patient appointment the
    // title is the selected patient's name (existing) or derived from the entered name on save
    // (new) — overwriting it with the type title would mislabel the appointment, and the field is
    // hidden for patient types so the user couldn't fix it.
    if (this.modeOf(nextType) === 'none') {
      this.form.get('title').setValue(nextType?.title ?? '');
    }
    this.saveError = null;
  }

  /** Clear every patient-acquisition input so a mode switch starts clean. */
  private resetPatientState() {
    this.patient = null;
    this.duplicates = [];
    this.duplicatesOpen = false;
    this.newPatientValidate = false;
    this.createdPatientId = null;
    this.form.get('patientId').setValue(null, {emitEvent: false});
    this.appointmentMetaData.reset();
    this.newPatientForm?.reset({
      title: '',
      firstName: '',
      surname: '',
      identityNumber: '',
      identityNumberConfirmation: '',
      email: {type: 0, label: '', emailAddress: '', emailAddressConfirm: ''},
      cell: {type: 2, label: 'Cell', number: ''},
    });
    // Undo any lock from a partial-create failure, then re-seed the booking mode (and the
    // matching enabled controls) for the next entry.
    this.newPatientForm?.enable({emitEvent: false});
    this.applySaveMode(this.defaultSaveMode());
  }
}
